import { motion, AnimatePresence } from "framer-motion";
import { CalendarClock, Send, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { sendEodDigest } from "../lib/api";

export default function TodayPanel({ data, onOpenStock }) {
  const [sending, setSending] = useState(false);
  const hits = data?.hits || [];
  const today = data?.today;

  const push = async () => {
    setSending(true);
    try {
      const res = await sendEodDigest();
      toast.success(`EOD digest sent · ${res.hits} breakouts`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed — configure Telegram first");
    } finally { setSending(false); }
  };

  return (
    <aside className="card-surface rounded-2xl p-5 lg:sticky lg:top-24 h-fit" data-testid="today-panel">
      <div className="flex items-center gap-2 mb-1">
        <CalendarClock className="h-4 w-4 text-[#FFB020]" />
        <h3 className="font-display text-base font-medium">Today&apos;s breakouts</h3>
      </div>
      <div className="font-mono text-[10px] tracking-widest text-white/45 mb-4">
        {today ? formatToday(today) : "—"} · {hits.length} signal{hits.length !== 1 ? "s" : ""}
      </div>

      <div className="space-y-2 max-h-[440px] overflow-y-auto pr-1">
        <AnimatePresence>
          {hits.length === 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="text-sm text-white/45 py-6 text-center" data-testid="today-empty">
              No breakouts today yet — next auto-scan at the top of the next hour + 20 min.
            </motion.div>
          )}
        </AnimatePresence>
        {hits.map((h, i) => (
          <motion.button
            key={`${h.symbol}-${h.signal_time}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            onClick={() => onOpenStock?.(h.symbol)}
            data-testid={`today-row-${h.symbol}`}
            className="w-full text-left rounded-lg border border-white/6 hover:border-[#FFB020]/40 bg-white/2 hover:bg-white/4 px-3 py-2.5 transition-colors"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="ticker-badge">{h.symbol}</span>
                <span className="ticker-badge text-[#00E5FF] border-[#00E5FF]/30 bg-[#00E5FF]/8">
                  ₹{h.level.toLocaleString("en-IN")}
                </span>
              </div>
              <span className="font-mono text-[10px] text-white/50 shrink-0">
                {timeIST(h.signal_time)}
              </span>
            </div>
            <div className="mt-1.5 flex items-center justify-between text-xs">
              <span className="text-white/50 truncate">{h.name}</span>
              <span className={`font-mono ${h.still_above_level ? "text-[#39FF14]" : "text-[#FFB020]"}`}>
                LTP ₹{h.ltp.toLocaleString("en-IN")}
              </span>
            </div>
            <div className="mt-1 font-mono text-[10px] text-white/40">
              SL ₹{h.stop_loss.toLocaleString("en-IN")} · Risk −{Math.abs(h.risk_pct).toFixed(2)}%
            </div>
          </motion.button>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-white/8">
        <button
          data-testid="eod-digest-btn"
          onClick={push}
          disabled={sending}
          className="pill-btn-primary w-full inline-flex items-center justify-center gap-2 text-sm disabled:opacity-50"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {sending ? "Sending…" : "Send EOD digest now"}
        </button>
        <p className="mt-2 font-mono text-[10px] text-white/40 leading-relaxed">
          Automatic dispatch at <span className="text-white/70">16:00 IST</span> every trading day, 30 min after market close, once Telegram is connected.
        </p>
      </div>
    </aside>
  );
}

function timeIST(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso; }
}

function formatToday(ymd) {
  try {
    return new Date(ymd + "T12:00:00+05:30").toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata", weekday: "short", day: "2-digit", month: "short",
    }).toUpperCase();
  } catch { return ymd; }
}
